import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight, ShieldCheck, Phone } from "lucide-react";
import { NAV_LINKS, BRAND } from "@/config";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <>
      {/* top utility bar */}
      <div className="fixed inset-x-0 top-0 z-50 hidden bg-[#0B1120] py-1.5 text-center text-xs text-slate-400 lg:block">
        <span className="inline-flex items-center gap-6">
          <span className="inline-flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5 text-[#2563EB]" /> ISO Certified &amp; Government Empanelled</span>
          <span className="text-slate-700">|</span>
          <a href={`tel:${BRAND.phoneRaw}`} className="inline-flex items-center gap-1.5 transition-colors hover:text-white"><Phone className="h-3.5 w-3.5 text-[#2563EB]" /> 24/7 Support: {BRAND.supportLine}</a>
        </span>
      </div>

      <motion.header
        initial={{ y: -80 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed left-0 right-0 z-50 transition-colors duration-300 lg:top-7 ${
          scrolled ? "top-0 border-b border-slate-800 bg-[#0B1120]/85 backdrop-blur-xl lg:top-0" : "top-0 bg-[#0B1120]/60 backdrop-blur-md lg:bg-transparent"
        }`}
      >
        <nav className="mx-auto flex max-w-7xl items-center justify-between px-6 py-3.5 lg:px-10">
          <Link to="/" data-testid="nav-logo" className="group flex items-center gap-2.5">
            <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#2563EB] transition-transform group-hover:rotate-6">
              <ShieldCheck className="h-5 w-5 text-white" />
            </span>
            <span className="font-display text-lg font-extrabold leading-none tracking-tight text-white">
              KAS <span className="text-[#60A5FA]">Info Tech (P) Ltd.</span>
              <span className="block text-[10px] font-medium uppercase tracking-[0.2em] text-slate-500">Security &amp; Surveillance</span>
            </span>
          </Link>

          <div className="hidden items-center gap-0.5 xl:flex">
            {NAV_LINKS.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                data-testid={`nav-link-${link.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `relative rounded-full px-3.5 py-2 text-sm font-medium transition-colors ${
                    isActive ? "text-white" : "text-slate-400 hover:text-white"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {link.label}
                    {isActive && (
                      <motion.span layoutId="nav-pill" className="absolute inset-0 -z-10 rounded-full bg-white/10" transition={{ type: "spring", stiffness: 350, damping: 30 }} />
                    )}
                  </>
                )}
              </NavLink>
            ))}
          </div>

          <div className="flex items-center gap-3">
            <Link
              to="/contact"
              data-testid="nav-contact-cta"
              className="group hidden items-center gap-1.5 rounded-full bg-[#2563EB] px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#3b82f6] sm:flex"
            >
              Free Site Survey
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen((v) => !v)}
              className="flex h-10 w-10 items-center justify-center rounded-full border border-slate-700 text-white xl:hidden"
              aria-label="Toggle menu"
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </nav>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 bg-[#0B1120]/97 backdrop-blur-xl xl:hidden"
          >
            <div className="flex h-full flex-col justify-center gap-1.5 px-8">
              {NAV_LINKS.map((link, i) => (
                <motion.div key={link.to} initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.05 * i }}>
                  <NavLink
                    to={link.to}
                    data-testid={`mobile-nav-link-${link.label.toLowerCase()}`}
                    className={({ isActive }) => `font-display text-3xl font-bold tracking-tight ${isActive ? "text-[#60A5FA]" : "text-white"}`}
                  >
                    {link.label}
                  </NavLink>
                </motion.div>
              ))}
              <Link to="/contact" data-testid="mobile-contact-cta" className="mt-8 inline-flex w-fit items-center gap-2 rounded-full bg-[#2563EB] px-6 py-3 text-base font-semibold text-white">
                Get a Free Site Survey <ArrowUpRight className="h-4 w-4" />
              </Link>
              <p className="mt-6 text-sm text-slate-500">{BRAND.supportLine} · {BRAND.email}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
